import type { RGB, ThreatLevel } from '@/types';
import { THREAT_COLORS } from '@/config';

/**
 * Get the RGB color for a threat level
 * @param threat - Threat level (0-5)
 * @returns RGB color object
 */
export function getThreatColorRGB(threat: number): RGB {
  const level = Math.min(5, Math.max(0, Math.floor(threat))) as ThreatLevel;
  return THREAT_COLORS[level];
}

/**
 * Get the RGBA color string for a threat level
 * @param threat - Threat level (0-5)
 * @param opacity - Opacity value (0-1)
 * @returns RGBA color string
 */
export function getThreatColor(threat: number, opacity: number = 1.0): string {
  const color = getThreatColorRGB(threat);
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;
}

/**
 * Convert RGB to hex color string
 * @param rgb - RGB color object
 * @returns Hex color string (e.g., "#ff0000")
 */
export function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/**
 * Interpolate between two RGB colors
 * @param color1 - First color
 * @param color2 - Second color
 * @param t - Interpolation factor (0-1)
 * @returns Interpolated RGB color
 */
export function interpolateColor(color1: RGB, color2: RGB, t: number): RGB {
  const clampedT = Math.max(0, Math.min(1, t));
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * clampedT),
    g: Math.round(color1.g + (color2.g - color1.g) * clampedT),
    b: Math.round(color1.b + (color2.b - color1.b) * clampedT),
  };
}
